import type { CapabilityOverride } from "@/services/capabilitiesApi";
import { isOverrideLive } from "@/utils/tripwireBadge";

export type CatalogCronBadgeKind =
  | "running"
  | "gate-paused"
  | "sweep-paused"
  | "paused"
  | "pending"
  | "error";

/**
 * Both catalog.cron.gate and catalog.cron.sweep are global-only capabilities
 * (source_scopable=False — see the plan), so the one row that matters is the
 * live global row: expired rows are treated as absent, mirroring the auto-
 * resume property the simulator's SQL reads directly from Postgres.
 */
export function selectGlobalRow(
  rows: CapabilityOverride[] | undefined,
): CapabilityOverride | undefined {
  return (rows ?? []).find(
    (row) => row.scope_type === "global" && isOverrideLive(row),
  );
}

export type CatalogCronRowStatus = "running" | "paused" | "pending" | "error";

/**
 * Per-row status for the Delta gate / Nightly sweep badges. Kept separate
 * from `catalogCronBadgeKind` (which reads both rows together) because one
 * query can fail or still be loading while the other has already resolved —
 * collapsing both rows onto the combined kind would show a stale "Running"
 * next to a row whose own fetch is pending or errored.
 */
export function catalogCronRowStatus(args: {
  row: CapabilityOverride | undefined;
  pending: boolean;
  error: boolean;
}): CatalogCronRowStatus {
  if (args.error) return "error";
  if (args.pending) return "pending";
  return args.row?.state === "deny" ? "paused" : "running";
}

export function catalogCronBadgeKind(args: {
  gateRow: CapabilityOverride | undefined;
  sweepRow: CapabilityOverride | undefined;
  gatePending: boolean;
  sweepPending: boolean;
  gateError: boolean;
  sweepError: boolean;
}): { kind: CatalogCronBadgeKind; title: string } {
  if (args.gateError || args.sweepError) {
    return {
      kind: "error",
      title: "Could not load catalog cron capability overrides.",
    };
  }

  if (args.gatePending || args.sweepPending) {
    return {
      kind: "pending",
      title: "Loading catalog cron capability overrides…",
    };
  }

  const gateDenied = args.gateRow?.state === "deny";
  const sweepDenied = args.sweepRow?.state === "deny";

  if (gateDenied && sweepDenied) {
    return {
      kind: "paused",
      title:
        "Nothing is generating on a schedule. Missing will keep climbing until this is turned back on.",
    };
  }

  if (gateDenied) {
    return {
      kind: "gate-paused",
      title:
        "New work waits for the 02:00 sweep, which is unbounded — expect a large single pass on the shared box.",
    };
  }

  if (sweepDenied) {
    return {
      kind: "sweep-paused",
      title:
        "Reconciliation and orphan tombstoning are paused. The gate still renders when the corpus moves, but a burst over 200 combos will leave a remainder nothing picks up.",
    };
  }

  return {
    kind: "running",
    title: "Both the delta gate and the nightly sweep are enabled.",
  };
}
